import Image from 'next/image';
import useSWR from 'swr';
import Link from 'next/link';
import { useMemo } from 'react';
import { TacoRating } from '@appsumo/dorado-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { DISCUSSION } from '~/lib/discussions/constants';
import { fetchJson } from '~/lib/fetch';
import { CommentType } from '~/static/discussions/enums';
import StatusTags from '~/components/discussions/DiscussionStatus';
import { ReviewPageProps } from '~/lib/discussions/types';
import { PDPContext } from '~/lib/product/context';
import { useOneTap } from '~/lib/script';
import { useUserStatus } from '~/hooks/userStatus';
import { AGGREGATE_PAGE_EXPERIMENT_NAME } from '~/lib/util/constants';
import { useExperiment } from '~/lib/experiment';

import { UserMeta, Actions, Children, Buttons } from '~/components/discussions';
import DiscussionSidebar from '~/components/discussions/DiscussionSidebar';
import { ReviewSeo } from '~/components/seo';
import { Breadcrumb, Breadcrumbs } from '../ui';

export const ReviewPageComponent = ({
  deal,
  discussion: fallbackData,
  discussionId,
}: ReviewPageProps) => {
  useOneTap();

  const { data: commentData, mutate } = useSWR(
    `/discussions/api/listcomments/${deal.id}/?group=${CommentType.Review}&thread_id=${discussionId}&for_discussion_page=True`,
    fetchJson,
    {
      fallbackData: fallbackData,
    },
  );

  const { userStatus } = useUserStatus(deal.id);
  const { comments } = commentData;
  const review = comments[0];
  const { user } = review;

  const items: Breadcrumb[] = useMemo(
    () => [
      { href: `/products/${deal.slug}/`, label: deal.public_name },
      {
        href: `/products/${deal.slug}/reviews/`,
        label: `${deal.public_name} Rating & Reviews`,
      },
      { label: review.title },
    ],
    [deal.public_name, deal.slug, review.title],
  );

  const { variant: aggregateVariant } = useExperiment(
    AGGREGATE_PAGE_EXPERIMENT_NAME,
  );

  return (
    <>
      <ReviewSeo review={review} deal={deal} />
      <PDPContext.Provider value={{ deal, userStatus }}>
        <div className="mx-auto w-full max-w-7xl grow px-8 md:my-12">
          {aggregateVariant === 'enabled' && (
            <Breadcrumbs
              items={items}
              className="mt-6 md:mb-4 lg:mb-6 xl:px-8"
            />
          )}

          <div className="-mx-8 mb-4 flex justify-between border-b p-4 px-8 sm:hidden">
            <h3 className="font-header text-2xl font-semibold">
              {deal.public_name}
            </h3>
            <Link
              href={`/products/${deal.slug}/`}
              className="flex items-center text-blue-600"
            >
              Product details
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="ml-2"
              />
            </Link>
          </div>
          <article className="mx-auto flex max-w-6xl gap-x-8 max-sm:flex-col">
            <div className="grow">
              {review.user_moderator && (
                <StatusTags
                  isApproved={review.approved}
                  isResolved={review.resolved}
                />
              )}

              <header className="mb-4">
                <h1 className={`mb-2 font-header text-3xl font-bold`}>
                  {review.rating && `${deal.public_name} Review: `}
                  {review.title}
                </h1>
                {review.rating && (
                  <TacoRating
                    averageRating={review.rating}
                    href={`/products/${deal.slug}/#reviews`}
                    size="md"
                    className="mt-4"
                  />
                )}
              </header>
              <main>
                <p className="leading-relaxed">{review.comment}</p>
                <div className="mt-4 flex gap-x-4">
                  <Image
                    src={user.avatar}
                    alt={user.username}
                    width={70}
                    height={70}
                    unoptimized
                    className="h-[70px] w-[70px] rounded-full max-sm:h-[40px] max-sm:w-[40px]"
                  />
                  <UserMeta
                    user={review.user}
                    posted={review.submit_date}
                    purchased={review.purchased}
                  />
                </div>
                <Buttons comment={review} mutateComment={mutate} isReview />
                <Actions
                  comment={review}
                  mutateComment={mutate}
                  moderator={userStatus?.user_moderator}
                />
                {review.children && review.children.length > 0 && (
                  <div className="pt-10">
                    <Children
                      root={review}
                      comments={review.children}
                      mutateComment={mutate}
                      moderator={userStatus?.user_moderator}
                      isReview
                    />
                  </div>
                )}
              </main>
            </div>
            <DiscussionSidebar
              deal={deal}
              discussionSlug={DISCUSSION.REVIEWS}
            />
          </article>
        </div>
      </PDPContext.Provider>
    </>
  );
};
